// Reactive per-screen store with declarative client persistence.
import type { Persist, StateDecl } from "./types";

export interface KVStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface StoreOpts {
  user: string;
  app: string;
  screen: string;
  session?: KVStorage;
  local?: KVStorage;
}

type Seg = string | number;

export function parsePath(path: string): Seg[] {
  const segs: Seg[] = [];
  let i = 0;
  const n = path.length;
  let buf = "";
  const flush = () => { if (buf !== "") { segs.push(buf); buf = ""; } };
  while (i < n) {
    const c = path[i];
    if (c === ".") { flush(); i++; }
    else if (c === "[") {
      flush();
      let j = i + 1, num = "";
      while (j < n && path[j] !== "]") { num += path[j]; j++; }
      segs.push(Number(num));
      i = j + 1;
    } else { buf += c; i++; }
  }
  flush();
  return segs;
}

export function getPath(root: unknown, path: string): unknown {
  let cur: unknown = root;
  for (const s of parsePath(path)) {
    if (cur === null || cur === undefined) return undefined;
    cur = (cur as any)[s as any];
  }
  return cur;
}

function clone<T>(v: T): T {
  if (v === null || typeof v !== "object") return v;
  return JSON.parse(JSON.stringify(v));
}

/** Immutable set: clones nodes along the path and returns a new root. */
export function setPath(root: any, path: string, value: unknown): any {
  const segs = parsePath(path);
  if (segs.length === 0) return root;
  const rootIsArr = typeof segs[0] === "number";
  const next = Array.isArray(root) ? root.slice() : root && typeof root === "object" ? { ...root } : (rootIsArr ? [] : {});
  let cur: any = next;
  for (let i = 0; i < segs.length - 1; i++) {
    const s = segs[i];
    const childIsArr = typeof segs[i + 1] === "number";
    const existing = cur[s as any];
    const copy = Array.isArray(existing) ? existing.slice()
      : existing && typeof existing === "object" ? { ...existing }
      : (childIsArr ? [] : {});
    cur[s as any] = copy;
    cur = copy;
  }
  cur[segs[segs.length - 1] as any] = value;
  return next;
}

export class Store {
  private state: Record<string, unknown> = {};
  private initials: Record<string, unknown> = {};
  private persistScope: Record<string, Persist> = {};
  private sensitive: Record<string, boolean> = {};
  private subs = new Set<() => void>();

  constructor(decls: Record<string, StateDecl> | undefined, private opts: StoreOpts) {
    for (const [k, d] of Object.entries(decls ?? {})) {
      this.persistScope[k] = d.persist ?? "none";
      this.sensitive[k] = !!d.sensitive;
      this.initials[k] = d.initial !== undefined ? d.initial : null;
      const persisted = this.readPersisted(k);
      this.state[k] = persisted !== undefined ? persisted : clone(this.initials[k]);
    }
  }

  snapshot(): Record<string, unknown> { return this.state; }
  get(path: string): unknown { return getPath(this.state, path); }

  set(path: string, value: unknown): void {
    this.state = setPath(this.state, path, value);
    this.persistTop(topKey(path));
    this.notify();
  }
  merge(path: string, value: Record<string, unknown>): void {
    const cur = (this.get(path) as Record<string, unknown>) ?? {};
    this.set(path, { ...cur, ...value });
  }
  append(path: string, value: unknown): void {
    const cur = (this.get(path) as unknown[]) ?? [];
    this.set(path, [...cur, value]);
  }
  removeAt(path: string, index: number): void {
    const cur = (this.get(path) as unknown[]) ?? [];
    this.set(path, cur.filter((_, i) => i !== index));
  }
  removeWhere(path: string, pred: (item: unknown, index: number) => boolean): void {
    const cur = (this.get(path) as unknown[]) ?? [];
    this.set(path, cur.filter((it, i) => !pred(it, i)));
  }
  toggle(path: string): void { this.set(path, !this.get(path)); }
  increment(path: string, by = 1): void { this.set(path, (Number(this.get(path)) || 0) + by); }
  clear(path: string): void {
    const segs = parsePath(path);
    if (segs.length === 1 && typeof segs[0] === "string" && segs[0] in this.initials) {
      this.set(path, clone(this.initials[segs[0]]));
    } else {
      this.set(path, null);
    }
  }

  subscribe(cb: () => void): () => void {
    this.subs.add(cb);
    return () => { this.subs.delete(cb); };
  }
  private notify(): void { for (const s of [...this.subs]) s(); }

  private storageFor(scope: Persist): KVStorage | undefined {
    if (scope === "local") return this.opts.local;
    if (scope === "session") return this.opts.session;
    return undefined;
  }
  private keyName(k: string): string {
    return `sdui:${this.opts.user}:${this.opts.app}:${this.opts.screen}:${k}`;
  }
  private persistTop(k: string): void {
    const scope = this.persistScope[k];
    if (!scope || scope === "none") return;
    if (scope === "local" && this.sensitive[k]) return; // fintech guard
    const st = this.storageFor(scope);
    if (!st) return;
    try { st.setItem(this.keyName(k), JSON.stringify(this.state[k])); } catch { /* quota/serialize */ }
  }
  private readPersisted(k: string): unknown {
    const scope = this.persistScope[k];
    if (!scope || scope === "none") return undefined;
    if (scope === "local" && this.sensitive[k]) return undefined;
    const st = this.storageFor(scope);
    if (!st) return undefined;
    const raw = st.getItem(this.keyName(k));
    if (raw == null) return undefined;
    try { return JSON.parse(raw); } catch { return undefined; }
  }
}

export function topKey(path: string): string {
  return String(parsePath(path)[0]);
}
